import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';
import { BasicListModel, Note, SpaceType, TextContent, ThesisContent } from '../../shared/models';
import { BaseCRMItem } from '../../shared/models/crm/CRMItem.model';
import { AbstractCRUD, CRUDService, ExceptionService, PaginatedList, UtilsService } from '../../xform-compat';
import { ContentService } from '../services';


@Injectable({
  providedIn: 'root'
})
export class CrmNoteService extends AbstractCRUD<BasicListModel> implements CRUDService<BasicListModel> {

  private exceptionsService: ExceptionService;
  private utilService: UtilsService;

  constructor(
    exceptionService: ExceptionService,
    http: HttpClient,
    utilsService: UtilsService,
    private contentService: ContentService,
  ) {
    super(exceptionService, http, utilsService);
    this.exceptionsService = exceptionService;
    this.utilService = utilsService;
  }

  public getResourcePath(): string {
    return '/rms';
  }

  public isCrmNote(textContent: TextContent | ThesisContent): boolean {
    return textContent.textContentType === 'CRM_NOTE';
  }

  public getLastNoteTime<T extends BaseCRMItem>(item: T): string {
    return item.notes?.map(n => n.textContent).map(tc => tc.timestamp).sort((a, b) => a < b ? 1 : -1)[0];
  }

  public findCrmItemByNoteId<T extends BaseCRMItem>(crmItems: Array<T>, textId: number): T {
    return crmItems && crmItems.find(crm => crm.notes.map(n => n.textContent.id).some(i => i === textId));
  }

  public getCrmItemTextContent<T extends BaseCRMItem>(crmItem: T, textContentId: number) {
    return crmItem && crmItem.notes && crmItem.notes.find(n => n.textContent.id === textContentId);
  }

  public getCrmNotes(crmId: number, params?: any): Observable<PaginatedList<Note>> {
    return this.contentService.formatInlineImageObservablePaginatedNote(this.getData(`${this.getApiPath()}/crm/${crmId}/notes`, params));
  }

  public getLatestOverviewNote(companyId: number): Observable<any> {
    return super.getHTTP().get(`${this.getApiPath()}/companies/${companyId}/spaces`)
      .pipe(concatMap((result: PaginatedList<SpaceType>) => this.getOverviewNoteForSpace(result)))
      .pipe(concatMap((result) => this.getOverviewNoteContent(result)))
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public createDraft(crmId: number, note: {content: string, isOverview: boolean, attachmentIds: Array<number>}) {
    note.content = this.contentService.formatInlineImageContentRemove(note.content);
    return super.getHTTP().post(`${this.getApiPath()}/crm/${crmId}/notes/draft`, note)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public updateNote(crmId: number, noteId: number , note: {content: string, isOverview: boolean, attachmentIds: Array<number>}) {
    note.content = this.contentService.formatInlineImageContentRemove(note.content);
    return super.getHTTP().put(`${this.getApiPath()}/crm/${crmId}/notes/${noteId}`, note)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public saveDraftForPosted(crmId: number, noteId: number, note: {content: string, isOverview: boolean, attachmentIds: Array<number>}) {
    note.content = this.contentService.formatInlineImageContentRemove(note.content);
    return super.getHTTP().post(`${this.getApiPath()}/crm/${crmId}/notes/${noteId}/draft`, note)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public discardNoteDraft(crmId: number, noteId: number) {
    return super.getHTTP().delete(`${this.getApiPath()}/crm/${crmId}/notes/${noteId}/draft/me`)
    .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getNoteHistory(crmId: number, noteId: number) {
    return super.getHTTP().get(`${this.getApiPath()}/crm/${crmId}/notes/${noteId}/history`)
    .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getAllNoteReferences(crmId: number) {
    return super.getHTTP().get(`${this.getApiPath()}/crm/${crmId}/notes/references`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getNoteReferences(crmId: number, noteId: number) {
    return super.getHTTP().get(`${this.getApiPath()}/crm/${crmId}/notes/${noteId}/references`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public updateNoteReferences(crmId: number, noteId: number, body: {references: Array<any> }) {
    return super.getHTTP().put(`${this.getApiPath()}/crm/${crmId}/notes/${noteId}/references`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getRecentNotes(crmIds: Array<number>) {
    let params = new HttpParams();
    crmIds.forEach(id => {
 params = params.append('crmId', `${id}`);
});
    return super.getHTTP().get(`${this.utilsService.getAPIPath()}/crm/item/recent/notes`,  { params: params })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  private getOverviewNoteForSpace(result: PaginatedList<SpaceType>): Observable<any> {
    const space = result.records.find(r => r.isDefault);
    if (!space) {
 return of(null);
}
    return super.getHTTP().get(`${this.getApiPath()}/spaces/${space.id}/notes/overview`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  private getOverviewNoteContent({spaceId, overviewNoteId}: {spaceId: number, page: number, overviewNoteId: number}): Observable<any> {
    if (!overviewNoteId || overviewNoteId < 0) {
 return of(null);
}
    return super.getHTTP().get(`${this.getApiPath()}/spaces/${spaceId}/notes/${overviewNoteId}`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  private getData(url: string, params?: any): Observable<any> {
    const httpParams: HttpParams = this.utilService.generateParams(params);
    return super.getHTTP().get(url, {params: httpParams}).pipe(catchError(ex => this.exceptionsService.catchBadResponse(ex)));
  }
}
